import React, { useState, useRef } from 'react';
import { type ICellRendererParams } from 'ag-grid-community';
import { resolveMappedColumnsForRow } from 'common/visualizations/views/agGridReact/helpers/tableActionsHelper';
import { TableActionsEmbedModal } from './TableActionsEmbedModal';
import { ForgePopover, ForgeIconButton, ForgeIcon } from '@tylertech/forge-react';
import { TableActionsContent } from './TableActionsContent';
import { type TableActionsConfigs } from 'common/visualizations/views/agGridReact/TableActionsButton/types';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  rowParams: ICellRendererParams;
  tableActionsContext: TableActionsConfigs;
  newTableActions: string[];
}

export const TableActionsViewButton: React.FC<Props> = ({
  rowParams,
  tableActionsContext,
  newTableActions,
}) => {
  const { mappedColumns } = tableActionsContext;
  const resolved = mappedColumns
    ? resolveMappedColumnsForRow(rowParams.node, rowParams.data, mappedColumns)
    : null;
  const mappedColumnsWithValue = resolved ? resolved.columns : null;

  const uniqueActionKey = mappedColumnsWithValue
    ? mappedColumnsWithValue.map((col) => col.value ?? '').join('-')
    : undefined;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const targetRef = useRef<HTMLDivElement>(null);

  if (!mappedColumns || mappedColumns.length === 0 || resolved?.ambiguous || resolved?.missing) {
    return null;
  }

  const onModalDismiss = () => {
    setShowModal(false);
    setSelectedUrl('');
  };

  const onLinkClick = (url: string) => {
    setShowModal(true);
    setSelectedUrl(url);
  };

  return (
    <div ref={targetRef} className="table-actions-view">
      <ForgeIconButton
        id={`table-action-button-trigger-${uniqueActionKey}`}
        data-testid="table-actions-view-button"
        className="tyler-icons"
        aria-label="table-actions-view-button"
      >
        <ForgeIcon name="more_vert" />
      </ForgeIconButton>
      <ForgePopover
        targetElementRef={targetRef}
        anchor={`table-action-button-trigger-${uniqueActionKey}-${uuidv4()}`}
      >
        <div className="table-actions-popup">
          <TableActionsContent
            contextParams={{
              ...tableActionsContext,
              mappedColumns: mappedColumnsWithValue,
            }}
            onLinkClick={onLinkClick}
            newTableActions={newTableActions}
          />
        </div>
      </ForgePopover>
      {showModal ? (
        <TableActionsEmbedModal url={selectedUrl} onDismiss={onModalDismiss} showModal={showModal} />
      ) : null}
    </div>
  );
};
